import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { API_URL } from 'data/services/apiConstants';

import { openModal } from 'reducers/modalReducer';
import { MODAL_TYPES } from 'view/ModalManager';
import ReportStreetViewImageButton from 'components/ReportStreetViewImageButton';

import './ImagesWidget.css';

function ImagesWidget({ thumbnailUrl, propertyData, zoningData, onBack }) {
	const dispatch = useDispatch();
	const [selectedImg, setSelectedImg] = useState(null);
	const carouselRef = useRef(null);

	let lunaImages = [];
	if (propertyData?.lunaImages) {
		lunaImages = propertyData.lunaImages;
	}

	if (propertyData?.photos) {
		lunaImages = (propertyData?.photos || []).map((photo) => ({
			...photo,
			thumbnail: photo?.url,
		}));
	}

	useEffect(() => {
		if (thumbnailUrl) {
			setSelectedImg({
				thumbnail: `${API_URL}${thumbnailUrl}`,
			});
		}
	}, [thumbnailUrl]);

	const onSelect = (image) => {
		setSelectedImg(image);
	};

	// Only the Street View "Current" thumbnail can be reported/regenerated --
	// historic (luna) photos aren't resolved by our Street View pipeline.
	const isCurrentStreetViewImage =
		!!thumbnailUrl && selectedImg?.thumbnail === `${API_URL}${thumbnailUrl}`;

	const onExpand = () => {
		dispatch(
			openModal(MODAL_TYPES.IMAGE, {
				src: selectedImg?.thumbnail || selectedImg?.mediumSizeLink,
				propertyId: isCurrentStreetViewImage ? propertyData?.id : null,
				copyRight: selectedImg?.collection ? (
					<div className="image-copyright">
						©
						<a
							className="link-underline-primary"
							href="https://nycrecords.access.preservica.com/"
							target="_blank"
							rel="noreferrer"
						>
							NYC Municipal Archives.
						</a>{' '}
						All rights reserved. Used for informational purposes only.
					</div>
				) : (
					<></>
				),
			})
		);
	};

	const onCarouselBackward = useCallback(() => {
		if (carouselRef.current) {
			carouselRef.current.scrollBy({ left: -150, behavior: 'smooth' });
		}
	}, []);

	const onCarouselForward = useCallback(() => {
		if (carouselRef.current) {
			carouselRef.current.scrollBy({ left: 150, behavior: 'smooth' });
		}
	}, []);

	return (
		<div className="images-widget">
			<div className="image-overlay-buttons">
				{onBack && (
					<button onClick={() => onBack()}>
						<i className="bi bi-x-lg" />
					</button>
				)}
				{isCurrentStreetViewImage && (
					<ReportStreetViewImageButton
						propertyId={propertyData?.id}
						iconOnly
					/>
				)}
				<button onClick={() => onExpand()}>
					<i className="bi bi-fullscreen" />
				</button>
			</div>
			<div className="main-image position-relative">
				{selectedImg && (
					<>
						<img
							className="street-view-image"
							src={selectedImg?.thumbnail || selectedImg?.mediumSizeLink}
							alt="Building"
						/>
					</>
				)}
			</div>
			<div className="thumbnail-carousel position-relative">
				<div
					className={`overly-buttons ${lunaImages?.length > 3 ? '' : 'd-none'}`}
				>
					<button
						className="btn btn-link left-arrow-button"
						onClick={() => onCarouselBackward()}
					>
						<i className="bi bi-arrow-left" />
					</button>
					<button
						className="btn btn-link right-arrow-button"
						onClick={() => onCarouselForward()}
					>
						<i className="bi bi-arrow-right" />
					</button>
				</div>

				<div className="thumbnails-scroll-wrapper" ref={carouselRef}>
					<div
						className={`thumbnail-wrapper position-relative ${
							selectedImg?.thumbnail === `${API_URL}${thumbnailUrl}`
								? 'selected'
								: ''
						}`}
						onClick={() =>
							onSelect({
								thumbnail: `${API_URL}${thumbnailUrl}`,
							})
						}
					>
						{thumbnailUrl && (
							<img
								className="thumb-image"
								src={`${API_URL}${thumbnailUrl}`}
								alt={`Street view`}
							/>
						)}
						<div className="thumb-collection">Current</div>
					</div>
					{lunaImages.map((image, i) => (
						<div
							className={`thumbnail-wrapper position-relative ${
								selectedImg?.thumbnail === image?.thumbnail ? 'selected' : ''
							}`}
							key={i}
							onClick={() => {
								onSelect(image);
							}}
						>
							{image.thumbnail && (
								<img
									className="thumb-image"
									src={image.thumbnail}
									alt={`Historic ${i}`}
								/>
							)}

							{image?.collection && (
								<div className="thumb-collection">
									{image.collection}
									{'s'}
								</div>
							)}
						</div>
					))}
				</div>
			</div>
		</div>
	);
}

export default ImagesWidget;
